import classNames from 'classnames';
import React from 'react';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { GET_CONTACT_CUSTOMER_DATA_QUERY, GET_CUSTOMER_AGREEMENTS_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import { ContactDataResponse, CustomerAgreementsResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { EMAIL_TYPES } from '../../../utils/filterEmailsByType';
import { FrontCarIcon, MobileIcon, ReceiptIcon, SingleCardIcon } from '../../svgs/ExpressonSVGs';
import FetchingAnimation from '../FetchingAnimation';
import Link from '../Link';

type QstarServicesProps = {
    className?: string
    hasCreditcard: boolean
    hasSmartcard: boolean
    hasBankcard: boolean
    isCompany: boolean
};

const QstarServices: React.FC<QstarServicesProps> = ({ className, hasCreditcard, hasSmartcard, hasBankcard, isCompany }) => {
    const applyForCardsurl = isCompany
        ? import.meta.env.VITE_VERIFIED_URL_COMPANY || 'https://qstar.se/foretagskort/#kort' // TODO: update meta variables here
        : import.meta.env.VITE_VERIFIED_URL_PRIVATE || 'https://qstar.se/medlem#kort'

    const BATCH_SIZE = 15; // Number of agreements to fetch per request
    const { data: agreementsData, loading: agreementsLoading, error: agreementsError } = useGetCustomer<CustomerAgreementsResponse>(
        GET_CUSTOMER_AGREEMENTS_QUERY,
        { first: BATCH_SIZE, after: null, before: null, last: null }
    );

    const { data: customerContactData, loading: customerContactLoading, error: customerContactError, refetch: refetchContactData } = useGetCustomer<ContactDataResponse>(
        GET_CONTACT_CUSTOMER_DATA_QUERY,
        undefined,
        { fetchPolicy: 'network-only' }
    );

    // Console log errors
    if (agreementsError) console.error('Failed to load agreements:', agreementsError.message)

    /**
     * Check if customer has approved the Q-Star App Terms & Conditions agreement
     * 
     * AGREEMENT IDs (approval.agreement.id values):
     *   7   = Reklam - Allow marketing communications
     *   8   = Tredjepartsreklam - Allow third-party marketing communications
     *   332 = Autogiro - Allow automatic payment (Autogiro)
     *   347 = Allmänna villkor - Informed of general terms for customer card
     *   350 = Kundklubb - Customer Club general terms
     *   354 = Användarvillkor Qstar App - Q-Star App Terms & Conditions
     * 
     * Note: We check agreement.id (not agreement.type.id) because agreement.id is unique per agreement
     */
    const hasAppAgreement = agreementsData?.currentCustomer?.agreementApprovals?.nodes?.some(
        approval => approval.agreement.id === 354
    ) ?? false;

    const vehicleSpecEmails = (customerContactData?.currentCustomer?.emails || [])
        .filter(e => e.type.id === EMAIL_TYPES.VEHICLE_SPECIFICATION);
    const hasActiveVehicleSpecification = vehicleSpecEmails.length > 0;

    return (
        <div className={`qstarservices-component ${className}`}>
            <div className="services-header">
                <h5 className="body-text">Anslutna tjänster</h5>
            </div>
            <div className="services-content">
                <div className="services-cards">
                    <span className="services-title">
                        Kort
                    </span>

                    {hasCreditcard &&
                        <div className="service-item">
                            <SingleCardIcon className={classNames('service-icon',
                                {
                                    'grey': isCompany,
                                    'red': !isCompany,
                                }
                            )} width={24} height={24} />
                            {isCompany ? 'Företagskort' : 'Tankkort'}
                        </div>
                    }

                    {hasSmartcard &&
                        <div className="service-item">
                            <SingleCardIcon className={classNames('service-icon', 'blue'
                            )} width={24} height={24} />
                            Smartkort
                        </div>
                    }

                    {hasBankcard &&
                        <div className="service-item">
                            <SingleCardIcon className={classNames('service-icon', 'black'
                            )} width={24} height={24} />
                            Bankkort
                        </div>
                    }

                    {!hasCreditcard && !hasSmartcard && !hasBankcard && (
                        <>
                            <p className="no-services-text">Du har inga kort kopplade till ditt konto.</p>
                            <Link href={applyForCardsurl} openNewTab={true}>Läs mer och ansök</Link>
                        </>
                    )}

                    {(hasCreditcard || hasSmartcard || hasBankcard) && (
                        <Link href="/kort">Visa alla kort</Link>
                    )}
                </div>

                <div className="services-digital">
                    <span className="services-title">
                        Digitala tjänster
                    </span>

                    {agreementsLoading ? (
                        <div className="service-item">
                            <FetchingAnimation width={24} height={24} />
                            <FetchingAnimation width={100} height={20} />
                        </div>


                    ) : hasAppAgreement ? (
                        <div className="service-item">
                            <MobileIcon width={24} height={24} />
                            App
                        </div>
                    ) : (
                        null // Return nothing if the agreement is not approved
                    )
                    }

                    {(hasCreditcard || hasSmartcard || hasBankcard || hasAppAgreement) && (
                        <div className="service-item">
                            <ReceiptIcon width={24} height={24} />
                            Kvittokopia
                        </div>
                    )}

                    {hasCreditcard && isCompany && hasActiveVehicleSpecification && (
                        <div className="service-item">
                            <FrontCarIcon width={24} height={24} />
                            Fordonspecifikation
                        </div>
                    )}
                </div>
            </div>


        </div>
    )
};

export default QstarServices;